import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { ListItem } from '@material-library';
import { Subscription } from 'rxjs';
import { NotificationsService } from 'src/app/core/notifications.service';
import { FileItemService } from 'src/app/file/file-item.service';
import { BlendEvent, FileItem, SYNC_URLS, WebpathActionType } from 'src/app/shared/models';
import { IDuplicateNameMap } from 'src/app/shared/models/move.model';
import { AtomicMoveService } from 'src/app/shared/services/atomic-move.service';
import { BatchCopyService } from 'src/app/shared/services/batch-copy.service';
import { BatchMoveService } from 'src/app/shared/services/batch-move.service';
import { BlendService } from 'src/app/shared/services/blend.service';
import { DirPickerService } from 'src/app/shared/services/dir-picker.service';
import { MoveActionsService } from 'src/app/shared/services/move-actions.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { IBatchAction, IBatchActionView, IFile, INameData } from 'src/sync';

@Component({
  selector: 'app-move-transfer',
  templateUrl: './move-transfer.component.html',
  styleUrls: ['./move-transfer.component.scss']
})
export class MoveTransferComponent implements OnInit, OnDestroy {
  @Input() selectedRows: FileItem[] = [];
  @Input() duplicateNameMap: IDuplicateNameMap = {};
  @Input() actionMoveOrCopy: WebpathActionType = WebpathActionType.MOVE;
  @Output() transferComplete = new EventEmitter<void>();

  public transferList: ListItem[] = [];

  private batchType: IBatchAction;
  public itemList: IFile[] = [];
  public destinationName = '';
  public batchView: IBatchActionView;
  countCompleted: number = 0;
  completedMap = {};
  public webPathActionType: typeof WebpathActionType = WebpathActionType;
  public desktopAppUrl = SYNC_URLS.DESKTOP_APP;
  public downloadAppUrl = `<p><a href="${this.desktopAppUrl}" target="_blank" style="color: blue; text-decoration: underline;">Download the desktop app</a> to manage items in the background while you continue working in Sync!</p>`;
  public showDownloadAppUrl = false;
  private showDownloadAppUrlTimer: number;
  private subscription: Subscription;
  private moveItemSubscribe: Subscription;

  constructor(
    private blendService: BlendService,
    private notificationsService: NotificationsService,
    private batchMoveService: BatchMoveService,
    private batchCopyService: BatchCopyService,
    private fileItemService: FileItemService,
    public dirPickerService: DirPickerService,
    private snackbarService: SnackbarService,
    private moveActionsService: MoveActionsService,
    private ngZone: NgZone,
    private atomicMoveService: AtomicMoveService
  ) { }

  async ngOnInit() {
    this.batchType = this.actionMoveOrCopy == WebpathActionType.COPY ? this.batchCopyService : (this.moveActionsService.isLinkPreserveAllowed ? this.atomicMoveService : this.batchMoveService);;
    this.batchView = this.batchType.view;
    this.itemList = [...this.selectedRows];
    this.getDestination();
    this.moveItemSubscribe = (<BatchMoveService | BatchCopyService>this.batchType).moveItemSubject.subscribe((data) => {
      if (data) {
        this.updateTransferList();
      }
    });
    this.subscription = this.atomicMoveService.percent$.subscribe((newPercent) => {
      this.batchView.percent = newPercent;
      this.updateTransferList();
    });
    this.startMoving()
  }

  ngOnDestroy() {
    clearTimeout(this.showDownloadAppUrlTimer);
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.moveItemSubscribe) {
      this.moveItemSubscribe.unsubscribe();
    }
  }

  public isCompleted(syncId: number) {
    return this.batchType.isCompleted(syncId);
  }

  startMoving = async () => {
    if (!this.dirPickerService.view.selected || !this.itemList.length) return;
    this.showDownloadAppUrlTimer = window.setTimeout(() => {
      this.ngZone.run(() => {
        this.showDownloadAppUrl = true;
      });
    }, 40 * 1000); // 40 seconds
    const finish = await this.moveActionsService.startMovingOrCopyExecution(this.dirPickerService.view.selected.sync_id, this.itemList, this.actionMoveOrCopy, this.duplicateNameMap)
    clearTimeout(this.showDownloadAppUrlTimer);
    this.finish(finish);
  }

  private getDestination() {
    this.destinationName = this.dirPickerService.getDestination();
  };

  finish = (success: boolean = true) => {
    const subTitle = this.itemList.length === 1 ? this.itemList[0].name : this.itemList.length > 1 ? `${this.itemList.length} items` : '';
    const message = ` “${subTitle}” to “${this.destinationName}”`;
    if (success && !this.batchView.globalCancel) {
      this.snackbarService.showMessage("success", (this.actionMoveOrCopy === WebpathActionType.MOVE ? 'Moved' : 'Copied') + message);
    } else if (this.batchView.globalCancel) {
      if (this.selectedRows.length === 1)
        this.snackbarService.showMessage("warning", "Canceled " + (this.actionMoveOrCopy === WebpathActionType.MOVE ? 'moving' : 'coping') + message);
      else {
        this.snackbarService.showMessage("success", (this.actionMoveOrCopy === WebpathActionType.MOVE ? 'Moved' : 'Copied') + ` ${this.countCompleted} out of ${this.selectedRows.length} items to “${this.destinationName}”`);
      }
    } else {
      this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
    }
    this.notificationsService.startNotificationLoop();
    this.transferComplete.emit();
  }

  public removeFromList = (listitem: IFile) => {
    this.itemList = this.itemList.filter(item => item.sync_id !== listitem.sync_id);
    this.batchType.view.syncIds = this.batchType.view.syncIds.filter(item => item !== listitem.sync_id);
    this.updateTransferList();
  }
  updateTransferList = () => {
    this.transferList = [];
    this.itemList.forEach((fileItem, ind) => {
      if (this.batchView.spinner == fileItem.sync_id && !this.isCompleted(fileItem.sync_id)) {
        this.countCompleted = ind;
        this.completedMap[fileItem.sync_id] = true;
      }
      this.transferList.push(
        {
          primaryText: fileItem.name,
          isSelected: false,
          isInteractive: true,
          leadAdornmentConfig: {
            enabled: true,
            type: 'list-head',
            size: 'large',
            data: fileItem,
            iconName: this.fileItemService.getFileTypeIcon(fileItem)
          },
          trail2AdornmentConfig: {
            enabled: true,
            type: (this.batchView.spinner == fileItem.sync_id) ? 'text' : 'icon',
            size: 'large',
            textContent: `${this.batchView.percent}%`,
            iconName: (this.batchView.spinner == fileItem.sync_id) ? `${this.batchView.percent}%` : (this.completedMap[fileItem.sync_id] ? 'checkmark' : 'close'),
            onClick: (this.completedMap[fileItem.sync_id]) ? () => { } : () => { this.removeFromList(fileItem) }
          }
        }
      )
    });
  }
}
